// AYA SEARCH — calibrage strict sur la maquette de référence (mobile-first, dense, premium).
// Icônes vectorielles lucide-react uniquement (aucun emoji UI). Logique moteur inchangée.

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Menu, Search, Mic, Camera, Landmark, ShoppingCart, MapPin, Plus,
  Home, Bookmark, User, Sparkles, MessageCircle, Scale, ExternalLink,
  ArrowRight, ArrowUp, Car, Store, X, Wrench, Flower2, KeyRound,
} from "lucide-react";
import BrvmTicker from "@/components/brvm/BrvmTicker";
import BrvmSearchHit from "@/components/brvm/BrvmSearchHit";
import TransportSection from "@/transport/TransportSection";
import RechargeHomeSection from "@/multiservice/RechargeHomeSection";
import AyaMenu from "@/components/AyaMenu";
import { useHomeTheme, HomeThemeToggle } from "@/components/HomeThemeToggle";
import { PersonalitySearchResult, SearchFamilies } from "@/pages/persona/PersonalitySearchResult";
import PlaceDiscovery from "@/pages/geo/PlaceDiscovery";
import FollowedCities from "@/pages/geo/FollowedCities";
import "./searchHomeTheme.css";
import SearchResultCard from "@/components/SearchResultCard";
import NewsFeed from "@/components/NewsFeed";
import { LocalPackMap } from "@/components/LocalPackMap";
import { listSaved, toggleSaved } from "@/lib/savedPlaces";
import { listSavedArticles } from "@/lib/savedArticles";
import { SEARCH_INTENTS } from "@/config/searchIntents";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
// URL officielle Kpandji : à renseigner via la configuration partenaire (NE PAS inventer).
const KPANDJI_SITE_URL = process.env.REACT_APP_KPANDJI_URL || "";

const CATEGORIES = [
  { value: "all", label: "Tous" },
  { value: "commerce", label: "Commerces" },
  { value: "sante", label: "Santé" },
  { value: "justice", label: "Justice" },
  { value: "divertissement", label: "Divertissement" },
  { value: "emploi", label: "Emploi" },
  { value: "administration", label: "Admin" },
  { value: "finance", label: "Finance" },
  { value: "geocoding", label: "Géocodage" },
];

const CAT_CARDS = [
  { value: "administration", label: "Services publics", route: "/services-publics", Icon: Landmark, color: "#E8F6EC", colorLight: "#0E7A4F", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)" },
  { value: "commerce", label: "Commerces", route: "/commerce", Icon: ShoppingCart, color: "#F97316", bg: "rgba(249,115,22,0.09)", border: "rgba(249,115,22,0.4)" },
  { value: "services", label: "Services", route: "/services", Icon: Wrench, color: "#FB923C", bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.4)" },
  { value: "bienetre", label: "Bien-être", route: "/bien-etre", Icon: Flower2, color: "#F472B6", bg: "rgba(236,72,153,0.10)", border: "rgba(236,72,153,0.4)" },
  { value: "transport", label: "Transport", route: "/transport", Icon: Car, color: "#34D399", colorLight: "#0E7A4F", bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.5)" },
  { value: "location", label: "Location", route: "/location", Icon: KeyRound, color: "#FDBA74", colorLight: "#F97316", bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.4)" },
  { value: "sante", label: "Santé", route: "/sante", Icon: Plus, color: "#EF5350", bg: "rgba(225,60,80,0.10)", border: "rgba(225,60,80,0.35)" },
  { value: "geocoding", label: "Lieux", Icon: MapPin, color: "#60A5FA", bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.35)" },
];

const NEWS_FALLBACK_PLACEHOLDER = "Rechercher en Côte d'Ivoire...";

// v5.7.0 — Carrousel "Découvrez la Côte d'Ivoire avec AYA" : 5 cartes éditoriales.
const DISCOVER_CARDS = [
  { id: "discover", img: "/aya/heroes/aya_hero_abidjan.jpg", sub: "Lieux, services, commerces, événements et bien plus encore.", cta: "Explorer maintenant", route: "/map-explorer" },
  { id: "artisans", img: "/aya/heroes/artisans_regions.jpg", title: "Découvrez les artisans de nos régions", sub: "Savoir-faire, créateurs et produits locaux de l'intérieur du pays.", cta: "Découvrir", q: "artisan" },
  { id: "gadji-celi", img: "/aya/heroes/concert_live_stage.jpg", title: "Concert Live — Gadji Celi", sub: "Événement de démonstration — billets dès 10 000 F.", cta: "Voir l'événement", q: "Concert Gadji Celi", testBadge: true },
  { id: "tresors", img: "/aya/heroes/tresors_regions.jpg", title: "Les trésors de nos régions", sub: "Gastronomie, artisanat, cultures et lieux d'exception.", cta: "Explorer", route: "/map-explorer" },
  { id: "bouge", img: "/aya/heroes/ca_bouge_events.jpg", title: "Ça bouge en Côte d'Ivoire", sub: "Concerts, événements, sorties et spectacles du moment.", cta: "Voir les événements", q: "concerts et événements" },
];

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("cat") || "all");
  const [results, setResults] = useState([]);
  const [shopHits, setShopHits] = useState([]); // Boutiques AYA PRO publiées correspondant à la requête.
  const [personality, setPersonality] = useState(null); // AYA ID — personnalité résolue (modèles A/B/C).
  const [families, setFamilies] = useState(null); // Familles de résultats par intention (Réf Coco).
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [webResults, setWebResults] = useState([]);
  const [webInfo, setWebInfo] = useState(null);
  const [fltOpen, setFltOpen] = useState(false);
  const [fltPrice, setFltPrice] = useState(null);
  const [fltRating, setFltRating] = useState(false); // { scope, available, error }
  const [localInfo, setLocalInfo] = useState(null); // { detected, category, label, ctx_filters }
  const [place, setPlace] = useState(null); // AYA Search V2 — fiche de découverte géographique
  const [fltCtx, setFltCtx] = useState(null); // string legacy OU facette {id,label,match[],attr?}
  const [facets, setFacets] = useState(null); // v5.18.0 — Dynamic Facets Engine (NIVEAU 1 + NIVEAU 2)
  const [fltExtra, setFltExtra] = useState({}); // filtres transversaux dynamiques actifs {id: facette}
  const [selectedId, setSelectedId] = useState(null);
  const [userPos, setUserPos] = useState(null);
  useEffect(() => {
    try {
      navigator.geolocation?.getCurrentPosition(
        (p) => setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {}, { timeout: 4000, maximumAge: 300000 },
      );
    } catch { /* géoloc indisponible — jamais inventée */ }
  }, []);
  const shownResults = results.filter((r) => passesSearchFilters(r, { price: fltPrice, rating: fltRating, open: fltOpen, ctx: fltCtx, extra: fltExtra, userPos }));
  const [aiSummary, setAiSummary] = useState(null); // null | "loading" | texte
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [voiceListening, setVoiceListening] = useState(false);
  // v5.7.0 — Placeholder animé (même barre, simple attribut placeholder) + favoris.
  const [animPh, setAnimPh] = useState(NEWS_FALLBACK_PLACEHOLDER);
  const [phActive, setPhActive] = useState(true);
  const [savedIds, setSavedIds] = useState(() => new Set(listSaved().map((s) => s.id)));
  // v5.12.0 — Badge compteur favoris (lieux + articles) sur l'onglet Enregistrés.
  const [savedCount, setSavedCount] = useState(() => listSaved().length + listSavedArticles().length);
  useEffect(() => {
    const refresh = () => setSavedCount(listSaved().length + listSavedArticles().length);
    window.addEventListener("aya-saved-changed", refresh);
    window.addEventListener("focus", refresh);
    return () => { window.removeEventListener("aya-saved-changed", refresh); window.removeEventListener("focus", refresh); };
  }, []);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [visualBusy, setVisualBusy] = useState(false);
  const recogRef = useRef(null);

  const isEmptyState = query.trim().length === 0;

  // v5.7.0 — Machine à écrire du placeholder : STOP au focus/saisie, reprise champ vide.
  useEffect(() => {
    if (!phActive || !isEmptyState) return undefined;
    let i = Math.floor(Math.random() * SEARCH_INTENTS.length);
    let pos = 0, deleting = false, t;
    const step = () => {
      const phrase = SEARCH_INTENTS[i];
      if (!deleting) {
        pos += 1; setAnimPh(phrase.slice(0, pos));
        t = setTimeout(step, pos >= phrase.length ? ((deleting = true), 900) : 34);
      } else {
        pos -= 1; setAnimPh(phrase.slice(0, pos) || " ");
        if (pos <= 0) { deleting = false; i = (i + 1) % SEARCH_INTENTS.length; t = setTimeout(step, 220); }
        else t = setTimeout(step, 14);
      }
    };
    t = setTimeout(step, 350);
    return () => clearTimeout(t);
  }, [phActive, isEmptyState]);

  // v5.7.0 — Favoris "Enregistrés" : toggle depuis n'importe quelle carte résultat.
  const toggleSaveResult = (r) => {
    const added = toggleSaved({
      id: r.id, name: r.name, category: r.category, category_label: r.category_label,
      commune_name: r.commune_name, link: r.link || null, image: r.image || r.item?.image_url || null,
      tel: r.tel || null, lat: r.lat ?? null, lng: r.lng ?? null, is_mock: !!r.is_mock,
    });
    setSavedIds((prev) => { const n = new Set(prev); if (added) n.add(r.id); else n.delete(r.id); return n; });
    toast.success(added ? "Enregistré ✓ — onglet Enregistrés" : "Retiré des enregistrés");
  };

  const goSearchQuery = (q) => { setCategory("all"); setQuery(q); runSearch(q, "all"); window.scrollTo({ top: 0 }); };

  // v5.7.3 — Bouton flottant orange "Retour en haut" (feed infini).
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { setShowTop(window.scrollY > 600); raf = 0; });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const scrollToTop = () => {
    const start = window.scrollY;
    const t0 = performance.now();
    const D = 260;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / D);
      const e = 1 - Math.pow(1 - p, 3);
      window.scrollTo(0, start * (1 - e));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // v5.7.1 — Carrousel découverte : dots synchronisés + auto-défilement (pause 8s après geste).
  const discoverRef = useRef(null);
  const discoverPauseRef = useRef(0);
  const [discoverIdx, setDiscoverIdx] = useState(0);
  const discoverStep = () => {
    const el = discoverRef.current;
    if (!el || el.children.length < 2) return el?.clientWidth || 1;
    return el.children[1].offsetLeft - el.children[0].offsetLeft;
  };
  const scrollToDiscover = (i) => {
    const el = discoverRef.current;
    if (!el) return;
    el.scrollTo({ left: i * discoverStep(), behavior: "smooth" });
  };
  useEffect(() => {
    if (!isEmptyState) return undefined;
    const t = setInterval(() => {
      if (Date.now() - discoverPauseRef.current < 8000) return;
      const el = discoverRef.current;
      if (!el) return;
      const cur = Math.round(el.scrollLeft / discoverStep());
      scrollToDiscover((cur + 1) % DISCOVER_CARDS.length);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyState]);

  useEffect(() => {
    document.title = query ? `Recherche : ${query} — AYA` : "AYA Search — L'Afrique au quotidien";
  }, [query]);

  useEffect(() => {
    const newParams = {};
    if (query.trim()) newParams.q = query.trim();
    if (category !== "all") newParams.cat = category;
    setParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, category]);

  const runSearch = useCallback(async (q, cat, forceWeb = false) => {
    const qTrim = String(q || "").trim();
    if (!qTrim) { setResults([]); setDuration(null); setWebResults([]); setWebInfo(null); setLocalInfo(null); setFacets(null); setShopHits([]); setPersonality(null); setFamilies(null); return; }
    // Recherches récentes — relance en un tap depuis l'écran d'accueil.
    try {
      const cur = JSON.parse(localStorage.getItem("aya_recent_searches") || "[]").filter((x) => x !== qTrim);
      localStorage.setItem("aya_recent_searches", JSON.stringify([qTrim, ...cur].slice(0, 5)));
    } catch (_) { /* stockage indisponible */ }
    setLoading(true);
    // Boutiques AYA PRO publiées — recherche en parallèle, non bloquante.
    axios.get(`${API}/boutique`, { params: { q: qTrim } })
      .then((b) => setShopHits(b.data?.ok ? b.data.shops : []))
      .catch(() => setShopHits([]));
    try {
      // AYA SEARCH ROUTER V1 : analyse d'intention + INTERNAL/WEB/HYBRID côté serveur.
      const r = await axios.get(`${API}/v3/search/v2`, {
        params: {
          q: qTrim, category: cat || "all",
          ...(forceWeb ? { scope: "web" } : {}),
          ...(userPos ? { ulat: userPos.lat, ulng: userPos.lng } : {}),
        },
      });
      if (r.data?.ok) {
        if (!forceWeb) {
          setResults(r.data.internal?.results || []);
          setPersonality(r.data.personality || null);
          setFamilies(r.data.families || null);
          setPlace(r.data.place || null);
          setLocalInfo(r.data.internal?.local || null);
          const fac = r.data.internal?.facets || null;
          setFacets(fac);
          setSelectedId(null);
          // Pré-activation : "avocat immobilier" → la facette Immobilier & foncier est déjà active.
          setFltCtx(fac?.active_id ? fac.subcategories.find((s) => s.id === fac.active_id) || null : null);
          // Contraintes exprimées dans la requête ("ouvert maintenant") → filtre pré-activé.
          setFltOpen(Boolean(fac?.active_filters?.includes("open_now")));
          setFltRating(false); setFltPrice(null); setFltExtra({});
        }
        setDuration(r.data.duration_ms || null);
        setWebResults(r.data.web?.results || []);
        setWebInfo({ scope: r.data.scope, available: r.data.web?.available, error: r.data.web?.error || null });
        // Synthèse IA asynchrone (n'attend pas les liens).
        if ((r.data.web?.results || []).length > 0) {
          setAiSummary("loading");
          axios.post(`${API}/v3/search/summarize`, { q: qTrim })
            .then((s) => setAiSummary(s.data?.ok ? String(s.data.summary).replace(/\*\*/g, "") : null))
            .catch(() => setAiSummary(null));
        } else setAiSummary(null);
      } else { setResults([]); setWebResults([]); setAiSummary(null); }
    } catch (e) {
      toast.error("Erreur recherche : " + (e.response?.data?.error || e.message));
      setResults([]); setWebResults([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPos]);

  useEffect(() => {
    const urlQ = params.get("q");
    if (urlQ) runSearch(urlQ, params.get("cat") || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (query.trim()) runSearch(query, category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 1) { setSuggestions([]); setShowSuggest(false); return; }
    // Intentions récentes correspondantes — affichées en tête du dropdown (🕘), plus de bloc dédié.
    let recents = [];
    try {
      recents = JSON.parse(localStorage.getItem("aya_recent_searches") || "[]")
        .filter((x) => x.toLowerCase().includes(q.toLowerCase()) && x.toLowerCase() !== q.toLowerCase())
        .slice(0, 3).map((x) => ({ name: x, category: "Récent", recent: true }));
    } catch (_) { /* vide */ }
    debounceRef.current = setTimeout(async () => {
      let sugg = [];
      if (q.length >= 2) {
        try {
          const r = await axios.get(`${API}/v3/search/suggest`, { params: { q, limit: 5 } });
          if (r.data?.ok) sugg = r.data.suggestions || [];
        } catch (_) { /* suggestions IA indisponibles */ }
      }
      const seen = new Set(recents.map((x) => x.name.toLowerCase()));
      const merged = [...recents, ...sugg.filter((s) => !seen.has(String(s.name).toLowerCase()))].slice(0, 7);
      setSuggestions(merged);
      // Ne jamais rouvrir le dropdown si l'utilisateur a déjà soumis / quitté le champ.
      setShowSuggest(merged.length > 0 && document.activeElement === inputRef.current);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSubmit = (e) => { if (e) e.preventDefault(); setShowSuggest(false); runSearch(query, category); };

  // Retire une intention récente de l'historique (croix du dropdown).
  const removeRecent = (name) => {
    try {
      const cur = JSON.parse(localStorage.getItem("aya_recent_searches") || "[]").filter((x) => x !== name);
      localStorage.setItem("aya_recent_searches", JSON.stringify(cur));
    } catch (_) { /* vide */ }
    setSuggestions((prev) => {
      const next = prev.filter((s) => !(s.recent && s.name === name));
      if (next.length === 0) setShowSuggest(false);
      return next;
    });
    inputRef.current?.focus();
  };

  // Historique visuel (choix B) : photos analysées stockées localement, relance en un tap sans ré-analyse.
  const [visualHistory, setVisualHistory] = useState([]);
  const [showVisualPanel, setShowVisualPanel] = useState(false);
  const loadVisualHistory = () => { try { const h = JSON.parse(localStorage.getItem("aya_visual_history") || "[]"); return Array.isArray(h) ? h : []; } catch { return []; } };
  const saveVisualEntry = (entry) => {
    const cur = [entry, ...loadVisualHistory().filter((x) => x.query !== entry.query)].slice(0, 6);
    try { localStorage.setItem("aya_visual_history", JSON.stringify(cur)); } catch (_) { /* quota */ }
    setVisualHistory(cur);
  };
  const removeVisualEntry = (ts) => {
    const cur = loadVisualHistory().filter((x) => x.ts !== ts);
    localStorage.setItem("aya_visual_history", JSON.stringify(cur));
    setVisualHistory(cur);
    if (cur.length === 0) setShowVisualPanel(false);
  };
  const openVisualPanel = () => {
    if (visualBusy) return;
    const h = loadVisualHistory();
    if (h.length === 0) { cameraInputRef.current?.click(); return; }
    setVisualHistory(h); setShowSuggest(false); setShowVisualPanel((v) => !v);
  };
  const relaunchVisual = (item) => {
    setShowVisualPanel(false);
    setQuery(item.query);
    toast.success(`📸 ${item.label}`);
    runSearch(item.query, category);
  };

  // Recherche visuelle : photo → gpt-4o-mini → intention → recherche.
  const handleVisualFile = async (file) => {
    if (!file) return;
    setVisualBusy(true);
    setShowVisualPanel(false);
    const tId = toast.loading("AYA analyse votre photo…");
    try {
      const { b64, thumb } = await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          const draw = (max, q) => {
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const cv = document.createElement("canvas");
            cv.width = Math.round(img.width * scale); cv.height = Math.round(img.height * scale);
            cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
            return cv.toDataURL("image/jpeg", q);
          };
          const full = draw(1024, 0.82);
          const mini = draw(110, 0.65);
          URL.revokeObjectURL(url);
          resolve({ b64: full.split(",")[1], thumb: mini });
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image illisible")); };
        img.src = url;
      });
      const r = await axios.post(`${API}/v3/search/visual`, { image_base64: b64, mime: "image/jpeg" });
      if (!r.data?.ok) throw new Error(r.data?.message || "Analyse impossible");
      toast.success(`📸 ${r.data.label}`, { id: tId });
      saveVisualEntry({ thumb, label: r.data.label, query: r.data.query, category: r.data.category, ts: Date.now() });
      setQuery(r.data.query); setShowSuggest(false);
      runSearch(r.data.query, category);
    } catch (e) {
      toast.error(e?.response?.data?.message || String(e.message || e), { id: tId });
    } finally {
      setVisualBusy(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) { if (e.key === "Enter") handleSubmit(e); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIdx >= 0 && suggestions[highlightIdx]) {
        const s = suggestions[highlightIdx];
        setQuery(s.name); setShowSuggest(false); runSearch(s.name, category);
      } else handleSubmit(e);
    } else if (e.key === "Escape") { setShowSuggest(false); setHighlightIdx(-1); }
  };

  const startVoice = () => {
    const SR = getSpeechRecognition();
    if (!SR) { toast.error("Recherche vocale non supportée par ce navigateur."); return; }
    if (voiceListening) { recogRef.current?.stop(); return; }
    const recog = new SR();
    recog.lang = "fr-FR"; recog.interimResults = false; recog.maxAlternatives = 1;
    recog.onstart = () => setVoiceListening(true);
    recog.onend = () => setVoiceListening(false);
    recog.onerror = () => { setVoiceListening(false); toast.error("Erreur micro."); };
    recog.onresult = (ev) => {
      const text = ev.results?.[0]?.[0]?.transcript || "";
      if (text) { setQuery(text); runSearch(text, category); }
    };
    recogRef.current = recog;
    try { recog.start(); } catch { setVoiceListening(false); }
  };

  const goAyaPrive = (ctx) => navigate(ctx ? `/aya-prive?ctx=${encodeURIComponent(ctx)}` : "/aya-prive");
  const [menuOpen, setMenuOpen] = useState(false);
  // 2026-06 — Thème Blanc/Vert partagé accueil + univers (blanc par défaut, préférence mémorisée).
  const [homeTheme, setHomeTheme] = useHomeTheme();
  const [menuNews, setMenuNews] = useState({ dot: false, liveActive: false, liveId: null });

  // Pastille menu : live en cours (banner public) + notifications non lues (si connecté)
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const [liveR, bellR] = await Promise.all([
          axios.get(`${API}/v3/live-banner/current`).catch(() => null),
          (() => {
            const tk = localStorage.getItem("civ_user_token");
            return tk ? axios.get(`${API}/v3/notifications/bell`, { headers: { Authorization: `Bearer ${tk}` } }).catch(() => null) : Promise.resolve(null);
          })(),
        ]);
        if (cancelled) return;
        const campaign = liveR?.data?.campaign || null;
        const liveId = campaign ? String(campaign.id || campaign._id || campaign.title || "live") : null;
        const liveNew = liveId && localStorage.getItem("aya_menu_seen_live") !== liveId;
        const unseen = bellR?.data?.unseen_count || 0;
        setMenuNews({ dot: Boolean(liveNew || unseen > 0), liveActive: Boolean(campaign), liveId });
      } catch { /* silencieux */ }
    };
    check();
    const iv = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const openMenu = () => {
    setMenuOpen(true);
    if (menuNews.liveId) localStorage.setItem("aya_menu_seen_live", menuNews.liveId);
    const tk = localStorage.getItem("civ_user_token");
    if (tk) axios.post(`${API}/v3/notifications/bell/ack`, {}, { headers: { Authorization: `Bearer ${tk}` } }).catch(() => {});
    setMenuNews((n) => ({ ...n, dot: false }));
  };
  const pickCategory = (v) => { setCategory(v); inputRef.current?.focus(); };
  const openKpandjiSite = () => {
    if (KPANDJI_SITE_URL) window.open(KPANDJI_SITE_URL, "_blank", "noopener");
    else toast.info("Le site officiel Kpandji sera relié dès la configuration du partenariat.");
  };

  return (
    <div data-testid="search-page" className="aya-home" data-aya-home={homeTheme} style={S.page}>
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} liveActive={menuNews.liveActive} />
      {/* ── HEADER compact : rangée hamburger / masque centré / Mode IA + avatar ── */}
      <header style={S.header}>
        <div style={S.headRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <button aria-label="Menu" data-testid="search-menu-btn" onClick={openMenu} style={{ ...S.hamburger, position: "relative" }}>
              {menuNews.dot && <span data-testid="menu-news-dot" style={{ position: "absolute", top: 4, right: 4, width: 9, height: 9, borderRadius: "50%", background: "#F97316", border: "1.5px solid #04120B", boxShadow: "0 0 6px rgba(249,115,22,0.8)" }} />}
              <Menu size={22} strokeWidth={2.4} />
            </button>
            {/* Toggle thème Blanc/Vert — à gauche du masque AYA */}
            <HomeThemeToggle theme={homeTheme} onChange={setHomeTheme} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button data-testid="mode-ia-toggle" aria-label="Activer le Mode IA" onClick={() => goAyaPrive()} style={S.modeIa}>
              <Sparkles size={13} color="#F97316" />
              <span style={{ whiteSpace: "nowrap" }}>Mode IA</span>
              <span style={S.toggleTrack}><span style={S.toggleDot} /></span>
            </button>
            <button aria-label="Profil" data-testid="search-avatar-btn" onClick={() => navigate("/mon-compte")} style={S.avatar}>C</button>
          </div>
        </div>
        <div style={S.headCenter}>
          <img src="/aya/splash.png" alt="Masque AYA" style={S.mask} />
          <div style={S.logoText}>AYA</div>
          <div style={S.tagline1}>L'AFRIQUE AU QUOTIDIEN</div>
          <div style={S.tagline2}>Rechercher&nbsp;&nbsp;·&nbsp;&nbsp;Comprendre&nbsp;&nbsp;·&nbsp;&nbsp;Agir</div>
        </div>
        <div style={S.signature} data-testid="signature-fierement-ivoirienne" aria-label="Fièrement Ivoirienne">
          <span style={S.sigFlag}>
            <span style={{ ...S.sigStripe, background: "#F77F00" }} />
            <span style={{ ...S.sigStripe, background: "#FFFFFF" }} />
            <span style={{ ...S.sigStripe, background: "#009E60" }} />
          </span>
          <div style={S.sigText}>Fièrement<br />Ivoirienne</div>
          <svg width="70" height="10" viewBox="0 0 92 12" style={S.sigStroke} aria-hidden="true">
            <path d="M2 8 C 22 3, 54 1.5, 90 5 C 58 6.5, 24 9, 3 10.5 Z" fill="#F97316" />
          </svg>
        </div>
      </header>

      <div style={S.container}>
        {/* ── SEARCH BAR compacte ── */}
        <form onSubmit={handleSubmit} style={S.searchWrap} data-testid="search-bar-form">
          <Search size={15} color="#F97316" style={{ flexShrink: 0 }} />
          <input
            ref={inputRef}
            data-testid="search-input"
            style={S.searchInput}
            placeholder={phActive && isEmptyState ? animPh : NEWS_FALLBACK_PLACEHOLDER}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { setPhActive(false); if (suggestions.length > 0) setShowSuggest(true); }}
            onBlur={() => { if (!query.trim()) setPhActive(true); setTimeout(() => setShowSuggest(false), 150); }}
            aria-label="Rechercher en Côte d'Ivoire"
          />
          {query.length > 0 && (
            <button type="button" data-testid="search-clear-btn" aria-label="Effacer la recherche"
              onClick={() => {
                setQuery(""); setFltCtx(null); setFltExtra({}); setFltOpen(false); setFltRating(false); setFltPrice(null);
                if (params.get("q")) setParams({}, { replace: true });
                inputRef.current?.focus();
              }}
              style={S.iconBtn}>
              <X size={15} color="#F87171" strokeWidth={2.6} />
            </button>
          )}
          <button type="button" data-testid="search-voice-btn" aria-label="Recherche vocale" onClick={startVoice} style={S.iconBtn}>
            <Mic size={15} color={voiceListening ? "#34D399" : homeTheme === "light" ? "#4A5D53" : "#CBD5C0"} />
          </button>
          <button type="button" data-testid="search-camera-btn" aria-label="Recherche visuelle" onClick={openVisualPanel} style={S.iconBtn}>
            <Camera size={15} color={visualBusy || showVisualPanel ? "#34D399" : homeTheme === "light" ? "#4A5D53" : "#CBD5C0"} style={visualBusy ? { animation: "pulse 1s ease-in-out infinite" } : undefined} />
          </button>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} data-testid="search-camera-input"
            onChange={(e) => handleVisualFile(e.target.files?.[0])} />
          {showVisualPanel && (
            <>
              <div onClick={() => setShowVisualPanel(false)} style={{ position: "fixed", inset: 0, zIndex: 1199 }} data-testid="visual-panel-backdrop" />
              <div style={{ ...S.suggestBox, zIndex: 1200, padding: "12px 12px 10px" }} data-testid="visual-history-panel">
                <div style={{ display: "flex", alignItems: "center", marginBottom: 9 }}>
                  <span style={{ flex: 1, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.1, color: "#6B7F72", textTransform: "uppercase" }}>📸 Photos analysées</span>
                  <button type="button" data-testid="visual-new-photo" onClick={() => { setShowVisualPanel(false); cameraInputRef.current?.click(); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 999, border: "none", background: "linear-gradient(180deg,#2ECC6B,#1DB954)", color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
                    <Camera size={12} /> Nouvelle photo
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                  {visualHistory.map((v, i) => (
                    <div key={v.ts} data-testid={`visual-history-item-${i}`} onClick={() => relaunchVisual(v)} role="button"
                      style={{ position: "relative", borderRadius: 11, overflow: "hidden", cursor: "pointer", border: "1px solid rgba(16,185,129,0.25)", aspectRatio: "4/3", background: "#0B1310" }}>
                      <img src={v.thumb} alt={v.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "10px 7px 5px", background: "linear-gradient(transparent, rgba(0,0,0,0.78))", fontSize: 9.5, fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.label}</div>
                      <span data-testid={`visual-history-remove-${i}`} role="button" aria-label={`Supprimer ${v.label}`}
                        onClick={(e) => { e.stopPropagation(); removeVisualEntry(v.ts); }}
                        style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: "50%", display: "grid", placeItems: "center", background: "rgba(0,0,0,0.6)", color: "#F87171", fontSize: 12, fontWeight: 900, cursor: "pointer" }}>×</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          <button type="submit" data-testid="search-submit-btn" aria-label="Lancer la recherche" style={S.greenBtn}>
            <Search size={14} color="#fff" strokeWidth={2.6} />
          </button>
          {showSuggest && (
            <div style={S.suggestBox} data-testid="search-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} type="button" data-testid={s.recent ? `suggest-recent-${i}` : `suggest-item-${i}`} style={{ ...S.suggestItem, display: "flex", alignItems: "center", gap: 6, background: i === highlightIdx ? "rgba(16,185,129,0.15)" : "transparent" }}
                  onMouseDown={(e) => { if (e.target?.dataset?.removeRecent) return; setQuery(s.name); setShowSuggest(false); runSearch(s.name, category); }}>
                  <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.recent ? "🕘 " : ""}{s.name} <span style={{ color: "var(--ah-muted)", fontSize: 11 }}>{s.category}</span></span>
                  {s.recent && (
                    <span data-remove-recent="1" data-testid={`suggest-recent-remove-${i}`} role="button" aria-label={`Retirer ${s.name} de l'historique`}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeRecent(s.name); }}
                      style={{ flexShrink: 0, width: 20, height: 20, borderRadius: "50%", display: "grid", placeItems: "center", color: "#F87171", fontSize: 13, fontWeight: 800, cursor: "pointer", background: "rgba(248,113,113,0.1)" }}>×</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </form>

        {isEmptyState ? (
          <>
            {/* ── 4 CATÉGORIES compactes ── */}
            <div style={S.catGrid} data-testid="search-categories">
              {CAT_CARDS.map((c) => (
                <button key={c.value} data-testid={`search-cat-${c.value}`}
                  onClick={() => (c.route ? navigate(c.route) : c.scrollTo ? document.getElementById(c.scrollTo)?.scrollIntoView({ behavior: "smooth", block: "start" }) : pickCategory(c.value))}
                  style={{ ...S.catCard, background: c.bg, borderColor: c.border }}>
                  <c.Icon size={15} color={homeTheme === "light" ? (c.colorLight || c.color) : c.color} strokeWidth={2.2} />
                  <span style={S.catLabel}>{c.label}</span>
                </button>
              ))}
            </div>

            {/* ── MES VILLES SUIVIES (diaspora) : météo réelle + actualités web ── */}
            <FollowedCities onOpen={(name) => setQuery(name)} />

            {/* ── SECTION RECHARGE TÉLÉPHONIQUE & INTERNET (AYA Multiservice, mock) ── */}
            <RechargeHomeSection navigate={navigate} />

            {/* ── SECTION TRANSPORT ── */}
            <TransportSection navigate={navigate} />

            {/* ── TICKER BRVM — fin, animé, données réelles → Espace BRVM ── */}
            <BrvmTicker />

            {/* ── DÉCOUVREZ LA CÔTE D'IVOIRE AVEC AYA — carrousel horizontal 5 cartes ── */}
            <div
              ref={discoverRef}
              style={{ ...S.carousel, marginTop: 11 }}
              data-testid="discover-carousel"
              onScroll={(e) => setDiscoverIdx(Math.min(DISCOVER_CARDS.length - 1, Math.max(0, Math.round(e.currentTarget.scrollLeft / discoverStep()))))}
              onTouchStart={() => { discoverPauseRef.current = Date.now(); }}
              onPointerDown={() => { discoverPauseRef.current = Date.now(); }}
              onWheel={() => { discoverPauseRef.current = Date.now(); }}
            >
              {DISCOVER_CARDS.map((c, ci) => (
                <div key={c.id} style={{ ...S.hero, marginTop: 0, flex: "0 0 82%", maxWidth: 340, scrollSnapAlign: "start" }}
                  data-testid={ci === 0 ? "search-hero" : `discover-card-${c.id}`}>
                  <img src={c.img} alt={c.title || "AYA — Côte d'Ivoire"} style={S.heroImg} loading="lazy" />
                  <div style={S.heroOverlay} />
                  {c.testBadge && (
                    <span data-testid={`discover-test-badge-${c.id}`} style={{ position: "absolute", top: 8, right: 8, padding: "2px 9px", borderRadius: 999, background: "#F59E0B", color: "#1A1206", fontSize: 9, fontWeight: 900, letterSpacing: 1 }}>TEST</span>
                  )}
                  <div style={S.heroContent}>
                    {ci === 0 ? (
                      <h1 style={S.heroTitle}>Découvrez<br />la Côte d'Ivoire<br />avec <span style={{ color: "#34D399" }}>AYA</span></h1>
                    ) : (
                      <h2 style={{ ...S.heroTitle, fontSize: 15.5 }}>{c.title}</h2>
                    )}
                    <p style={S.heroSub}>{c.sub}</p>
                    <button data-testid={ci === 0 ? "hero-explore-btn" : `discover-cta-${c.id}`}
                      onClick={() => (c.route ? navigate(c.route) : goSearchQuery(c.q))} style={S.heroBtn}>
                      {c.cta} <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Points de pagination du carrousel découverte */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 8 }} data-testid="discover-dots">
              {DISCOVER_CARDS.map((c, i) => (
                <button key={c.id} data-testid={`discover-dot-${i}`} aria-label={`Carte ${i + 1}`}
                  onClick={() => { discoverPauseRef.current = Date.now(); scrollToDiscover(i); }}
                  style={{ width: discoverIdx === i ? 18 : 7, height: 7, borderRadius: 999, border: "none", cursor: "pointer", padding: 0, background: discoverIdx === i ? "var(--ah-accent)" : "var(--ah-dot)", transition: "width .25s, background-color .25s" }} />
              ))}
            </div>

            {/* ── Fin de la découverte → respiration éditoriale avant les Actualités ── */}
            <div style={{ height: 16 }} aria-hidden="true" />
            <div style={{ height: 1, background: "var(--ah-divider)", margin: "0 4px" }} data-testid="news-editorial-separator" />

            {/* ── ACTUALITÉS — flux vertical infini (vraies actus via Brave backend) ── */}
            <div style={S.feedHead}>
              <h2 style={S.feedTitle} data-testid="news-section-title">Actualités</h2>
              <button data-testid="feed-more-btn" onClick={() => navigate("/kiosque")} style={S.feedMore}>
                Voir plus <ArrowRight size={13} />
              </button>
            </div>
            {/* v5.7.3 — Feed pleine largeur : marges latérales réduites (uniquement Actualités) */}
            <div style={{ margin: "0 -8px" }} data-testid="news-fullwidth-wrap">
            <NewsFeed renderAd={() => (
              <article style={{ ...S.kpandjiCard, flex: "none", maxWidth: "none" }} data-testid="kpandji-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <span style={S.sponsoBadge}>SPONSORISÉ</span>
                  <span style={{ textAlign: "right" }}>
                    <span style={{ color: "#D4AF37", fontWeight: 800, fontSize: 12, letterSpacing: 1, display: "block" }}>KPANDJI</span>
                    <span style={{ color: "var(--ah-muted, #9DB4A6)", fontSize: 8, letterSpacing: 0.6 }}>L'AUTOMOBILE IVOIRIENNE</span>
                  </span>
                </div>
                <h3 style={S.kpandjiTitle}>Découvrez <span style={{ color: "#D4AF37" }}>Kpandji</span> avec <span style={{ color: "#34D399" }}>AYA</span></h3>
                <p style={S.kpandjiSub}>Modèles, caractéristiques, tarifs et comparaison avec d'autres marques.</p>
                <img src="/aya/heroes/kpandji_suv.jpg" alt="Véhicule Kpandji" style={S.kpandjiImg} loading="lazy" />
                <div style={S.kpandjiBtns}>
                  <button data-testid="kpandji-know-btn" onClick={() => goAyaPrive("Je souhaite découvrir Kpandji, l'automobile ivoirienne : modèles, caractéristiques et tarifs.")} style={{ ...S.kBtn, background: "#0E7A4F", borderColor: "#10B981" }}>
                    <MessageCircle size={13} /> Connaître Kpandji
                  </button>
                  <button data-testid="kpandji-compare-btn" onClick={() => goAyaPrive("Je souhaite comparer les véhicules Kpandji avec d'autres marques, de façon factuelle.")} style={S.kBtn}>
                    <Scale size={13} /> Comparer Kpandji
                  </button>
                  <button data-testid="kpandji-site-btn" onClick={openKpandjiSite} style={S.kBtn}>
                    <ExternalLink size={13} /> Découvrir le site
                  </button>
                </div>
              </article>
            )} />
            </div>
          </>
        ) : (
          <>
            {facets?.subcategories?.length > 0 ? (
              /* ── v5.17.0 — HIÉRARCHIE : "QUE cherchez-vous ?" (sous-catégories métier) ── */
              <>
                <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 2.2, color: "var(--ah-accent)", padding: "4px 2px 2px", textTransform: "uppercase" }} data-testid="facet-title">
                  {facets.title}
                </div>
                <div style={S.chipsRow} data-testid="facet-subcategories">
                  <button data-testid="facet-all" onClick={() => setFltCtx(null)}
                    style={{ ...S.chip, fontWeight: 800, ...(!fltCtx?.id ? S.chipOn : {}) }}>
                    {facets.all_label}
                  </button>
                  {facets.subcategories.map((s) => (
                    <button key={s.id} data-testid={`facet-sub-${s.id}`}
                      onClick={() => setFltCtx((v) => (v?.id === s.id ? null : s))}
                      style={{ ...S.chip, fontWeight: 800, ...(fltCtx?.id === s.id ? { ...S.chipOn, background: "rgba(16,185,129,0.3)", boxShadow: "0 0 0 1px #10B981" } : {}) }}>
                      {s.label} <span style={{ opacity: 0.65, fontWeight: 600 }}>({s.count})</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div style={S.chipsRow} data-testid="search-filter-chips">
                {CATEGORIES.map((c) => (
                  <button key={c.value} data-testid={`chip-${c.value}`} onClick={() => setCategory(c.value)}
                    style={{ ...S.chip, ...(category === c.value ? S.chipOn : {}) }}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            {/* ── NIVEAU 2 — FILTRES TRANSVERSAUX : "COMMENT affiner ?" ── */}
            <div style={{ display: "flex", gap: 7, overflowX: "auto", padding: "2px 0 4px", scrollbarWidth: "none", ...(facets ? { opacity: 0.95 } : {}) }} data-testid="search-result-filters">
              {facets?.secondary_facets?.length > 0 ? (
                /* v5.18.0 — Dynamic Facets Engine : filtres pilotés par le backend selon l'intention. */
                facets.secondary_facets.map((f) => {
                  if (f.type === "price") {
                    return [5000, 15000, 50000].map((p) => (
                      <button key={`price-${p}`} data-testid={`filter-price-${p}`} onClick={() => setFltPrice((v) => (v === p ? null : p))}
                        style={{ ...S.chip, ...(fltPrice === p ? S.chipOn : {}), borderColor: fltPrice === p ? "#10B981" : "rgba(249,115,22,0.35)" }}>
                        ≤ {p.toLocaleString("fr-FR")} F
                      </button>
                    ));
                  }
                  const on = f.id === "open_now" ? fltOpen : f.id === "rating" ? fltRating : Boolean(fltExtra[f.id]);
                  const toggle = () => {
                    if (f.id === "open_now") setFltOpen((v) => !v);
                    else if (f.id === "rating") setFltRating((v) => !v);
                    else setFltExtra((prev) => { const n = { ...prev }; if (n[f.id]) delete n[f.id]; else n[f.id] = f; return n; });
                  };
                  return (
                    <button key={f.id} data-testid={f.id === "open_now" ? "filter-open-now" : f.id === "rating" ? "filter-rating-4" : `filter-${f.id.replace(/_/g, "-")}`}
                      onClick={toggle}
                      style={{ ...S.chip, ...(on ? S.chipOn : {}), borderColor: on ? "#10B981" : "rgba(249,115,22,0.35)" }}>
                      {f.label}
                    </button>
                  );
                })
              ) : (
                <>
                  <button data-testid="filter-open-now" onClick={() => setFltOpen((v) => !v)}
                    style={{ ...S.chip, ...(fltOpen ? S.chipOn : {}), borderColor: fltOpen ? "#10B981" : "rgba(249,115,22,0.35)" }}>
                    🕐 Ouvert maintenant
                  </button>
                  <button data-testid="filter-rating-4" onClick={() => setFltRating((v) => !v)}
                    style={{ ...S.chip, ...(fltRating ? S.chipOn : {}), borderColor: fltRating ? "#10B981" : "rgba(249,115,22,0.35)" }}>
                    ⭐ 4 et +
                  </button>
                  {(localInfo?.ctx_filters || []).map((c) => (
                    <button key={c} data-testid={`filter-ctx-${c.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setFltCtx((v) => (v === c ? null : c))}
                      style={{ ...S.chip, ...(fltCtx === c ? S.chipOn : {}), borderColor: fltCtx === c ? "#10B981" : "rgba(249,115,22,0.35)" }}>{c}</button>
                  ))}
                  {[5000, 15000, 50000].map((p) => (
                    <button key={p} data-testid={`filter-price-${p}`} onClick={() => setFltPrice((v) => (v === p ? null : p))}
                      style={{ ...S.chip, ...(fltPrice === p ? S.chipOn : {}), borderColor: fltPrice === p ? "#10B981" : "rgba(249,115,22,0.35)" }}>
                      ≤ {p.toLocaleString("fr-FR")} F
                    </button>
                  ))}
                </>
              )}
              {(fltOpen || fltRating || fltPrice != null || fltCtx || Object.keys(fltExtra).length > 0) && (
                <button data-testid="filter-reset" onClick={() => { setFltOpen(false); setFltRating(false); setFltPrice(null); setFltCtx(null); setFltExtra({}); }}
                  style={{ ...S.chip, color: "#F87171", borderColor: "rgba(239,68,68,0.4)" }}>✕ Effacer</button>
              )}
            </div>
            {loading && <div style={S.loadingTxt}>Recherche en cours…</div>}
            {/* AYA SEARCH V2 — fiche de découverte géographique (présentation, carte, transport, mairie, associés, liens) */}
            {!loading && duration != null && place && (
              <PlaceDiscovery place={place} navigate={navigate} commerceCount={results.length} commerces={results.slice(0, 12)}
                onSearch={goSearchQuery}
                onSeeCommerces={() => document.querySelector("[data-testid='local-pack-header'], [data-testid='search-meta']")?.scrollIntoView({ behavior: "smooth" })} />
            )}
            {!loading && duration != null && localInfo?.detected && results.length > 0 && (
              <div data-testid="local-pack-header" style={{ margin: "10px 2px 10px" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ah-text)" }}>
                  {shownResults.length} {localInfo.label} trouvé{shownResults.length > 1 ? "s" : ""}
                  {shownResults.length !== results.length ? ` (sur ${results.length})` : ""}
                </div>
                <div style={{ fontSize: 12, color: "var(--ah-muted)", marginTop: 3 }} data-testid="local-pack-synthesis">
                  {(() => {
                    const open = shownResults.filter(isResultOpenNow).length;
                    const book = shownResults.filter((r) => r.bookable).length;
                    return [`${open} ouvert${open > 1 ? "s" : ""} actuellement`, book > 0 ? `${book} avec prise de RDV directe dans AYA` : null].filter(Boolean).join(" · ");
                  })()}
                </div>
              </div>
            )}
            {!loading && duration != null && !localInfo?.detected && results.length > 0 && (
              <div style={{ color: "var(--ah-muted)", fontSize: 12, margin: "8px 2px" }} data-testid="search-meta">
                Résultats AYA : {shownResults.length}{shownResults.length !== results.length ? ` (sur ${results.length})` : ""} · {duration} ms
              </div>
            )}
            {!loading && localInfo?.detected && results.length > 0 && (
              <LocalPackMap results={shownResults} selectedId={selectedId} onSelect={setSelectedId} />
            )}
            {!loading && results.length > 0 && shownResults.length === 0 && <div style={S.loadingTxt} data-testid="search-no-filter-results">Aucun résultat avec ces filtres — essayez de les assouplir.</div>}
            {/* AYA ID — personnalité résolue (standard / artiste / immersif présidentiel) */}
            {!loading && duration != null && personality && <PersonalitySearchResult p={personality} />}
            {/* Familles de résultats par intention (Connaissances / Images / Commerce / Lieux) */}
            {!loading && duration != null && <SearchFamilies families={families} />}
            {/* Boutiques AYA PRO publiées correspondant à la requête */}
            {!loading && duration != null && shopHits.length > 0 && (
              <div style={{ margin: "10px 0 4px" }} data-testid="search-shops-block">
                <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: 2.2, color: "var(--ah-accent)", padding: "4px 2px 6px", textTransform: "uppercase" }}>🏪 Boutiques AYA</div>
                {shopHits.map((b) => (
                  <button key={b.slug} onClick={() => navigate(`/boutique/${b.slug}`)} data-testid={`search-shop-${b.slug}`}
                    style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left", background: "var(--ah-surface)", border: "1px solid var(--ah-border)", borderRadius: 14, padding: "10px 12px", marginBottom: 8, cursor: "pointer", fontFamily: "inherit" }}>
                    {b.logo_image ? <img src={b.logo_image} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} /> : <span style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 }}>🏪</span>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--ah-text)", display: "flex", gap: 7, alignItems: "center" }}>
                        {b.name}
                        {b.is_live && <span style={{ fontSize: 9, fontWeight: 900, background: "#DC2626", color: "#fff", borderRadius: 999, padding: "2px 8px" }}>🔴 LIVE</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ah-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.rating ? `★ ${String(b.rating).replace(".", ",")} (${b.reviews_count}) · ` : ""}{b.city || b.tagline}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 800, color: "var(--ah-accent)", flexShrink: 0 }}>Visiter →</span>
                  </button>
                ))}
              </div>
            )}
            {!loading && duration != null && results.length === 0 && (
              <div style={{ margin: "12px 2px" }} data-testid="search-no-aya-results">
                <div style={{ color: "var(--ah-muted)", fontSize: 13, fontWeight: 700 }}>Aucun établissement AYA trouvé{localInfo?.detected ? " dans cette zone" : ""}.</div>
                {webResults.length === 0 && (
                  <button
                    data-testid="search-web-fallback-btn"
                    onClick={() => runSearch(query, category, true)}
                    style={{ marginTop: 9, padding: "10px 16px", borderRadius: 999, border: "1px solid rgba(249,115,22,0.5)", background: "rgba(249,115,22,0.1)", color: "#F97316", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}
                  >🌐 Rechercher sur le Web</button>
                )}
              </div>
            )}
            {!loading && <BrvmSearchHit query={query} />}
            <div style={S.resultsGrid}>
              {shownResults.map((r) => {
                const inner = <SearchResultCard result={r} navigate={navigate} />;
                return (
                  <div
                    key={r.id || r.name}
                    data-testid={`local-result-${r.id || r.name}`}
                    onClick={() => setSelectedId(r.id)}
                    style={{ position: "relative", borderRadius: 18, outline: selectedId === r.id ? "2px solid #F97316" : "none", outlineOffset: 2 }}
                  >
                    {r.is_mock && (
                      <span data-testid={`test-badge-${r.id}`} style={{
                        position: "absolute", top: 8, right: 8, zIndex: 5,
                        padding: "3px 10px", borderRadius: 999, background: "#F59E0B",
                        color: "#1A1206", fontSize: 9.5, fontWeight: 900, letterSpacing: 1,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
                      }}>TEST</span>
                    )}
                    <button
                      data-testid={`save-btn-${r.id}`}
                      aria-label={savedIds.has(r.id) ? "Retirer des enregistrés" : "Enregistrer"}
                      onClick={(e) => { e.stopPropagation(); toggleSaveResult(r); }}
                      style={{
                        position: "absolute", top: 8, left: 8, zIndex: 5, width: 30, height: 30,
                        borderRadius: "50%", border: "1px solid rgba(16,185,129,0.4)",
                        background: "rgba(4,18,11,0.82)", display: "flex", alignItems: "center",
                        justifyContent: "center", cursor: "pointer",
                      }}>
                      <Bookmark size={14} color={savedIds.has(r.id) ? "#F59E0B" : "#CFE5D6"} fill={savedIds.has(r.id) ? "#F59E0B" : "none"} />
                    </button>
                    {inner}
                  </div>
                );
              })}
            </div>
            {/* ── RÉSULTATS WEB (Brave) — section distincte, jamais mélangée ── */}
            {!loading && webInfo && (webInfo.scope === "web" || webInfo.scope === "hybrid") && (
              <div data-testid="search-web-section" style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "0 2px 8px" }}>
                  <span style={{ width: 3, height: 16, borderRadius: 2, background: "#F97316" }} />
                  <span style={{ fontSize: 13, fontWeight: 800, color: "var(--ah-text)" }}>🌐 Résultats Web</span>
                </div>
                {/* ✨ Synthèse IA au-dessus des liens */}
                {aiSummary && webResults.length > 0 && (
                  <div data-testid="search-ai-summary" style={{
                    padding: "11px 13px", marginBottom: 10, borderRadius: 12,
                    background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(249,115,22,0.05))",
                    border: "1px solid rgba(16,185,129,0.35)",
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: "#2FCF62", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>✨ Synthèse AYA</div>
                    {aiSummary === "loading"
                      ? <div style={{ fontSize: 12, color: "var(--ah-muted)" }}>AYA analyse les résultats…</div>
                      : <div style={{ fontSize: 12.5, color: "var(--ah-text)", lineHeight: 1.55 }}>{aiSummary}</div>}
                  </div>
                )}
                {webResults.length === 0 ? (
                  <div style={{ ...S.loadingTxt, fontSize: 12 }} data-testid="search-web-unavailable">
                    {webInfo.available === false || webInfo.error ? "Résultats Web temporairement indisponibles." : "Aucun résultat Web."}
                  </div>
                ) : (
                  webResults.map((w, i) => (
                    <a key={w.url || i} href={w.url} target="_blank" rel="noreferrer" data-testid={`web-result-${i}`} style={{
                      display: "block", padding: "10px 12px", marginBottom: 8, borderRadius: 12, textDecoration: "none",
                      background: "var(--ah-surface)", border: "1px solid rgba(249,115,22,0.28)",
                    }}>
                      <div style={{ fontSize: 10, color: "#F97316", fontWeight: 700, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.domain}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ah-text)", lineHeight: 1.25 }}>{w.title}</div>
                      {w.description && <div style={{ fontSize: 11.5, color: "var(--ah-muted)", marginTop: 3, lineHeight: 1.4 }}>{w.description.slice(0, 160)}{w.description.length > 160 ? "…" : ""}</div>}
                    </a>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── NAVIGATION BASSE légère ── */}
      {/* v5.7.3 — Retour en haut : cercle ORANGE flottant au-dessus de la nav basse. */}
      <button
        data-testid="back-to-top-btn"
        aria-label="Retour en haut"
        onClick={scrollToTop}
        style={{
          position: "fixed", right: 14, bottom: "calc(84px + env(safe-area-inset-bottom))", zIndex: 55,
          width: 46, height: 46, borderRadius: "50%", border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #FB923C, #F97316)", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 6px 20px rgba(249,115,22,0.45)",
          opacity: showTop ? 1 : 0,
          transform: showTop ? "scale(1) translateY(0)" : "scale(0.6) translateY(10px)",
          pointerEvents: showTop ? "auto" : "none",
          transition: "opacity .22s ease, transform .22s ease",
        }}
      >
        <ArrowUp size={22} strokeWidth={2.6} />
      </button>

      <nav style={S.bottomNav} data-testid="search-bottom-nav">
        {[
          { Icon: Home, label: "Accueil", active: true, go: () => { setQuery(""); window.scrollTo(0, 0); } },
          { Icon: Search, label: "Rechercher", go: () => inputRef.current?.focus() },
          { Icon: Store, label: "Kiosque", kiosque: true, go: () => navigate("/kiosque") },
          { Icon: Bookmark, label: "Enregistrés", badge: savedCount, go: () => navigate("/enregistres") },
          { Icon: User, label: "Profil", go: () => navigate("/mon-compte") },
        ].map((t) => t.kiosque ? (
          <button key={t.label} data-testid="bottomnav-kiosque" onClick={t.go} aria-label="Kiosque"
            style={{ ...S.navItem, padding: "0 12px", marginTop: -16, color: "#F97316" }}>
            <span style={S.kiosqueCircle}><t.Icon size={19} color="#fff" strokeWidth={2.2} /></span>
            <span style={{ fontSize: 10, fontWeight: 700 }}>{t.label}</span>
          </button>
        ) : (
          <button key={t.label} data-testid={`bottomnav-${t.label}`} onClick={t.go} aria-label={t.label}
            style={{ ...S.navItem, ...(t.active ? S.navActive : {}), position: "relative" }}>
            {t.badge > 0 && (
              <span data-testid="saved-count-badge" style={{
                position: "absolute", top: 2, left: "50%", marginLeft: 6, minWidth: 16, height: 16,
                borderRadius: 999, background: "#F97316", color: "#fff", fontSize: 9.5, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px",
                boxShadow: "0 0 0 2px var(--ah-nav-ring)",
              }}>{t.badge > 99 ? "99+" : t.badge}</span>
            )}
            <t.Icon size={19} strokeWidth={t.active ? 2.4 : 2} />
            <span style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

// v4.13.0 — Filtres résultats : prix max, note minimum, ouvert maintenant.
function isResultOpenNow(r) {
  if (r.item?.always_open) return true;
  const h = r.item?.horaires || r.hours || null;
  if (!h) return false;
  const m = String(h).match(/(\d{1,2})\s*h.*?(\d{1,2})\s*h/);
  if (!m) return false;
  const now = new Date().getHours();
  const o = parseInt(m[1], 10); const c = parseInt(m[2], 10);
  return c > o ? (now >= o && now < c) : (now >= o || now < c);
}

function kmBetween(pos, r) {
  const rad = Math.PI / 180; const R = 6371;
  const dLat = (r.lat - pos.lat) * rad; const dLng = (r.lng - pos.lng) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(pos.lat * rad) * Math.cos(r.lat * rad) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function resultHaystack(r) {
  return `${r.name || ""} ${r.category_label || ""} ${r.subcategory || ""} ${(r.specialties || []).join(" ")} ${r.description || ""} ${r.address || ""} ${r.item?.category_label || ""} ${r.item?.description || ""} ${r.extra ? JSON.stringify(r.extra) : ""}`
    .toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}
function passesSearchFilters(r, { price, rating, open, ctx, extra, userPos }) {
  if (price != null) {
    const p = r.price_from ?? r.item?.tarif_fcfa ?? null;
    if (p == null || Number(p) > price) return false;
  }
  if (rating) {
    const n = r.rating ?? r.item?.rating ?? null;
    if (n == null || Number(n) < 4) return false;
  }
  if (open && !isResultOpenNow(r)) return false;
  // Placement partenaire (générique) : épinglé par le backend sur son domaine —
  // jamais évincé par une sous-catégorie métier (les filtres factuels s'appliquent, eux).
  if (ctx && !r.partner_placement) {
    // v5.18.0 — facette attribut exact (ex. hôtels : {attr:{key:'stars', value:4}}).
    if (ctx.attr) {
      if (Number(r.extra?.[ctx.attr.key] ?? NaN) !== Number(ctx.attr.value)) return false;
    } else {
      const hay = resultHaystack(r);
      // ctx = string (legacy) OU facette métier {match: [...]}.
      const needles = (Array.isArray(ctx.match) ? ctx.match : [String(ctx)])
        .map((n) => n.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
      if (!needles.some((n) => hay.includes(n))) return false;
    }
  }
  // v5.18.0 — filtres transversaux dynamiques (Dynamic Facets Engine).
  for (const f of Object.values(extra || {})) {
    if (f.id === "nearby") {
      if (!userPos || !Number.isFinite(r.lat) || !Number.isFinite(r.lng)) return false;
      if (kmBetween(userPos, r) > 5) return false;
    } else if (f.id === "appointment" || f.id === "book") {
      if (!(r.bookable || r.capabilities?.appointment || r.capabilities?.booking || r.capabilities?.book)) return false;
    } else {
      const hay = resultHaystack(r);
      const needles = (f.terms || [f.label]).map((n) => String(n).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
      if (!needles.some((n) => hay.includes(n))) return false;
    }
  }
  return true;
}

// v4.12.0 — Carte boutique (marketplace / mini-site Pro / mock) → mini-site unifié.
// v5.13.0 — ShopResultCard et ResultCard remplacés par le composant universel SearchResultCard.

const S = {
  page: { minHeight: "100vh", background: "var(--ah-page-bg)", color: "var(--ah-text)", fontFamily: "inherit", paddingBottom: "calc(84px + env(safe-area-inset-bottom))" },
  container: { maxWidth: 880, margin: "0 auto", padding: "0 14px" },
  header: { maxWidth: 908, margin: "0 auto", padding: "6px 14px 0", position: "relative" },
  signature: { position: "absolute", right: 14, top: 58, transform: "rotate(-9deg)", pointerEvents: "none", textAlign: "center" },
  sigFlag: { position: "absolute", top: -10, right: -12, display: "flex", width: 20, height: 12, transform: "skewX(-14deg) rotate(6deg)", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.45)" },
  sigStripe: { flex: 1, height: "100%" },
  sigText: { fontFamily: "'Caveat', cursive", fontWeight: 700, fontSize: 16, lineHeight: 0.92, color: "#2FCF62", textShadow: "0 1px 6px rgba(0,0,0,0.5)", whiteSpace: "nowrap" },
  sigStroke: { display: "block", margin: "1px auto 0" },
  headRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  hamburger: { background: "none", border: "none", color: "var(--ah-text)", cursor: "pointer", padding: 4, display: "flex" },
  headCenter: { textAlign: "center", marginTop: -20 },
  mask: { width: 62, height: 62, objectFit: "contain", filter: "drop-shadow(0 0 14px rgba(212,175,55,0.4))", display: "block", margin: "0 auto" },
  logoText: { fontSize: 21, fontWeight: 900, letterSpacing: 2.5, color: "var(--ah-title)", marginTop: -8, lineHeight: 1 },
  tagline1: { fontSize: 8, letterSpacing: 3, color: "var(--ah-text)", fontWeight: 700, marginTop: 2 },
  tagline2: { fontSize: 10, color: "var(--ah-muted)", marginTop: 1 },
  modeIa: { display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 4px 0 10px", borderRadius: 999, border: "1px solid var(--ah-border-strong)", background: "var(--ah-surface-elev)", color: "var(--ah-text)", fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  toggleTrack: { width: 28, height: 16, borderRadius: 999, background: "#10B981", position: "relative", display: "inline-block", flexShrink: 0 },
  toggleDot: { position: "absolute", right: 2, top: 2, width: 12, height: 12, borderRadius: "50%", background: "#fff" },
  avatar: { width: 29, height: 29, borderRadius: "50%", border: "1.5px solid var(--ah-border-strong)", background: "var(--ah-surface-elev)", color: "var(--ah-text)", fontWeight: 800, fontSize: 12, cursor: "pointer", flexShrink: 0 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center", gap: 6, marginTop: 9, height: 48, padding: "0 6px 0 15px", borderRadius: 999, background: "var(--ah-surface-input)", border: "1.5px solid var(--ah-border-strong)", boxShadow: "var(--ah-shadow)" },
  searchInput: { flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--ah-text)", fontSize: 13.5, minWidth: 0 },
  iconBtn: { background: "none", border: "none", cursor: "pointer", padding: "4px 3px", display: "flex", alignItems: "center" },
  greenBtn: { width: 33, height: 33, borderRadius: "50%", border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", cursor: "pointer", boxShadow: "0 3px 10px rgba(16,185,129,0.4)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  suggestBox: { position: "absolute", top: 48, left: 0, right: 0, zIndex: 30, background: "var(--ah-surface-elev)", border: "1px solid var(--ah-border-strong)", borderRadius: 14, overflow: "hidden" },
  suggestItem: { display: "block", width: "100%", textAlign: "left", padding: "10px 15px", border: "none", color: "var(--ah-text)", fontSize: 13.5, cursor: "pointer" },
  catGrid: { display: "flex", gap: 8, marginTop: 10, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 2 },
  catCard: { flex: "0 0 22.5%", minWidth: 82, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, height: 52, padding: "0 4px", borderRadius: 12, border: "1px solid", cursor: "pointer", color: "var(--ah-text)" },
  catLabel: { fontSize: 10, fontWeight: 600, textAlign: "center", lineHeight: 1.15 },
  indRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 9 },
  indCard: { padding: "7px 10px", borderRadius: 13, background: "var(--ah-surface)", border: "1px solid rgba(16,185,129,0.22)", display: "flex", flexDirection: "column", gap: 1 },
  indTitle: { fontSize: 11, fontWeight: 700, color: "var(--ah-soft)", display: "flex", alignItems: "center", gap: 4 },
  indValue: { fontSize: 15.5, fontWeight: 800, color: "var(--ah-title)", lineHeight: 1.2 },
  hero: { position: "relative", marginTop: 11, borderRadius: 17, overflow: "hidden", height: 172 },
  heroImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  heroOverlay: { position: "absolute", inset: 0, background: "linear-gradient(100deg, rgba(3,15,9,0.88) 28%, rgba(3,15,9,0.25) 62%, transparent)" },
  heroContent: { position: "relative", padding: "12px 14px", maxWidth: 240 },
  heroTitle: { fontSize: 17.5, lineHeight: 1.15, fontWeight: 800, margin: 0, color: "#FFFFFF", textShadow: "0 1px 8px rgba(0,0,0,0.45)" },
  heroSub: { color: "#E6F2E9", fontSize: 10.5, lineHeight: 1.4, margin: "6px 0 9px", textShadow: "0 1px 6px rgba(0,0,0,0.5)" },
  heroBtn: { display: "inline-flex", alignItems: "center", gap: 5, height: 31, padding: "0 13px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 11.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(16,185,129,0.4)" },
  feedHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "22px 0 14px" },
  feedTitle: { fontSize: 20, fontWeight: 900, margin: 0, color: "var(--ah-title)", letterSpacing: 0.2 },
  feedMore: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "var(--ah-accent)", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  carousel: { display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory", paddingBottom: 6, WebkitOverflowScrolling: "touch", scrollbarWidth: "none" },
  kpandjiCard: { flex: "0 0 78%", maxWidth: 330, scrollSnapAlign: "start", borderRadius: 15, padding: 10, background: "var(--ah-card-grad)", border: "1.5px solid var(--ah-border-strong)" },
  sponsoBadge: { display: "inline-block", padding: "3px 9px", borderRadius: 999, background: "#D4AF37", color: "#14100A", fontSize: 9, fontWeight: 800, letterSpacing: 0.7, height: "fit-content" },
  kpandjiTitle: { fontSize: 15, fontWeight: 800, margin: "6px 0 3px", color: "var(--ah-title)" },
  kpandjiSub: { color: "var(--ah-muted)", fontSize: 10.5, lineHeight: 1.35, margin: "0 0 7px" },
  kpandjiImg: { width: "100%", height: 88, objectFit: "cover", borderRadius: 10 },
  kpandjiBtns: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 8 },
  kBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 34, padding: "0 4px", borderRadius: 10, border: "1px solid var(--ah-border-strong)", background: "var(--ah-surface-elev)", color: "var(--ah-text)", fontSize: 9.5, fontWeight: 700, lineHeight: 1.15, cursor: "pointer", textAlign: "center" },
  newsCard: { flex: "0 0 56%", maxWidth: 225, scrollSnapAlign: "start", borderRadius: 15, overflow: "hidden", background: "var(--ah-surface)", border: "1px solid rgba(16,185,129,0.22)" },
  newsImg: { width: "100%", height: 92, objectFit: "cover" },
  newsTag: { display: "inline-block", margin: "8px 11px 0", padding: "2px 8px", borderRadius: 999, color: "#04120B", fontSize: 8.5, fontWeight: 800 },
  newsTitle: { margin: "6px 11px 5px", fontSize: 12.5, lineHeight: 1.3, color: "var(--ah-text)", fontWeight: 700 },
  chipsRow: { display: "flex", gap: 7, overflowX: "auto", padding: "12px 0 4px", scrollbarWidth: "none" },
  chip: { whiteSpace: "nowrap", padding: "7px 13px", borderRadius: 999, borderWidth: 1, borderStyle: "solid", borderColor: "var(--ah-border)", background: "var(--ah-surface)", color: "var(--ah-soft)", fontSize: 12, cursor: "pointer" },
  chipOn: { background: "var(--ah-chipon-bg)", borderColor: "#10B981", color: "var(--ah-text)", fontWeight: 700 },
  loadingTxt: { textAlign: "center", color: "var(--ah-muted)", padding: "24px 0", fontSize: 13.5 },
  resultsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginTop: 6 },
  resultCard: { padding: 13, borderRadius: 14, background: "var(--ah-surface)", border: "1px solid rgba(16,185,129,0.22)" },
  actionBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 11px", borderRadius: 9, border: "1px solid var(--ah-border)", background: "transparent", color: "var(--ah-soft)", fontSize: 11.5, cursor: "pointer" },
  bottomNav: { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 60, display: "flex", justifyContent: "space-around", padding: "5px 4px calc(6px + env(safe-area-inset-bottom))", background: "var(--ah-nav-bg)", backdropFilter: "blur(16px)", borderTop: "1px solid rgba(16,185,129,0.22)" },
  navItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "none", border: "none", color: "var(--ah-nav-item)", cursor: "pointer", padding: "5px 12px", borderRadius: 12 },
  navActive: { color: "var(--ah-nav-active-text)", background: "var(--ah-nav-active-bg)" },
  kiosqueCircle: { width: 42, height: 42, borderRadius: "50%", background: "linear-gradient(135deg,#FB923C,#EA580C)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(249,115,22,0.5)", border: "2.5px solid var(--ah-nav-ring)" },
};
